import React, { useMemo } from 'react';
import StepIcon from './StepIcon';

const OptimizationStepper = ({ job }) => {
  const activeIdx = useMemo(() => {
    if (!job) return 0;
    if (job.status === 'completed' || job.progress >= 100) return 3;
    return Math.min(2, Math.floor(job.progress / 33.334));
  }, [job]);

  const stepProgress = useMemo(() => {
    if (!job) return 0;
    if (job.status === 'completed' || job.progress >= 100) return 100;

    const p = (job.progress - activeIdx * 33.33) / 33.33;
    const clampedP = Math.max(0, Math.min(1, p));

    if (clampedP <= 0.3125) {
      return (clampedP / 0.3125) * 45;
    } else if (clampedP <= 0.4375) {
      return 45;
    } else if (clampedP <= 0.75) {
      return 45 + ((clampedP - 0.4375) / (0.75 - 0.4375)) * 40;
    } else if (clampedP <= 0.875) {
      return 85;
    } else {
      return 85 + ((clampedP - 0.875) / (1 - 0.875)) * 15;
    }
  }, [job, activeIdx]);

  const steps = [
    {
      key: 'setup',
      title: 'Reviewing the plan',
      subtitle: 'Aligning with your selected skills...',
      stepKey: 'setup',
    },
    {
      key: 'rewrite',
      title: 'Polishing your experience',
      subtitle: 'Improving bullets in your voice...',
      stepKey: 'rewrite',
    },
    {
      key: 'generate',
      title: 'Creating your documents',
      subtitle: 'Formatting files for download...',
      stepKey: 'generate',
    },
  ];

  return (
    <div className="process-container" role="status" aria-live="polite">
      {/* Upper Area: The Stack */}
      <div className="process-stack-container">
        {steps.map((step, idx) => {
          const isCompleted = idx < activeIdx;
          const isCurrent = idx === activeIdx;
          if (isCompleted) return null;

          const pendingIdx = idx - activeIdx;
          const offsetStyles = {
            transform: `translateY(${pendingIdx * -8}px) scale(${1 - pendingIdx * 0.04})`,
            opacity: pendingIdx === 0 ? 1 : pendingIdx === 1 ? 0.6 : 0.3,
            zIndex: 10 - pendingIdx,
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), z-index 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          };

          return (
            <div key={step.key} className={`process-card ${isCurrent ? 'active' : 'pending'}`} style={offsetStyles}>
              <StepIcon stepKey={step.stepKey} status={isCurrent ? 'active' : 'pending'} progress={stepProgress} />
              <div className="process-card-content">
                <h4 className="process-card-title">{step.title}</h4>
                {isCurrent && (
                  <p className="process-card-subtitle">
                    {step.subtitle} ({Math.min(100, Math.floor(stepProgress))}%)
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lower Area: Completed List */}
      <div className="process-completed-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {steps.map((step, idx) => {
          const isCompleted = idx < activeIdx;
          if (!isCompleted) return null;

          return (
            <div key={step.key} className="process-card completed animate-slide-down">
              <StepIcon stepKey={step.stepKey} status="completed" />
              <div className="process-card-content">
                <h4 className="process-card-title">{step.title}</h4>
                <p className="process-card-subtitle" style={{ color: 'var(--color-accent-ferrari)', opacity: 0.8 }}>Completed</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OptimizationStepper;
